// Which restaurants a support agent opened, and how often, kept on this device.
//
// Key exports: `readRecentTeamIds`, `readOpenCounts`, `recordOpenedTeam`,
// `sortTeams`, `SortMode`.
// Neighbours: `components/RestaurantGrid.tsx` sorts with it; `hooks/useOpenRestaurant.ts`
// records each open.
//
// The server does not say when a restaurant was last opened or how often, so the
// picker keeps its own most-recent-first list of team ids and a count per id. A
// restaurant never opened here sorts after every opened one, by name. Storage can
// be blocked: every read and write falls back to empty.

const RECENT_KEY = 'synvya_support_recent_team_ids'
const COUNTS_KEY = 'synvya_support_open_counts'
const MAX_RECENT = 50

export type SortMode = 'recent' | 'name' | 'frequency'

export function readRecentTeamIds(): number[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((n): n is number => typeof n === 'number') : []
  } catch {
    return []
  }
}

/** Times each restaurant was opened on this device, keyed by team id. */
export function readOpenCounts(): Record<number, number> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(COUNTS_KEY) ?? '{}')
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const counts: Record<number, number> = {}
    for (const [id, n] of Object.entries(parsed)) {
      if (typeof n === 'number' && n > 0) counts[Number(id)] = n
    }
    return counts
  } catch {
    return {}
  }
}

/** Move `id` to the front of the recent list and count one more open. */
export function recordOpenedTeam(id: number): void {
  try {
    const recent = [id, ...readRecentTeamIds().filter((n) => n !== id)].slice(0, MAX_RECENT)
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent))
    const counts = readOpenCounts()
    localStorage.setItem(COUNTS_KEY, JSON.stringify({ ...counts, [id]: (counts[id] ?? 0) + 1 }))
  } catch {
    /* storage blocked: the order falls back to name */
  }
}

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name)

/**
 * `recent`: opened restaurants first, most recent first, then the rest by name.
 * `name`: A to Z. `frequency`: most opened first, ties by recency, then by name.
 * Does not mutate `teams`.
 */
export function sortTeams<T extends { id: number; name: string }>(
  teams: readonly T[],
  mode: SortMode,
  recent: readonly number[] = readRecentTeamIds(),
  counts: Readonly<Record<number, number>> = readOpenCounts(),
): T[] {
  const rank = new Map(recent.map((id, i) => [id, i]))
  const byRecency = (a: T, b: T) => {
    const ra = rank.get(a.id)
    const rb = rank.get(b.id)
    if (ra !== undefined && rb !== undefined) return ra - rb
    if (ra !== undefined) return -1
    if (rb !== undefined) return 1
    return byName(a, b)
  }
  const sorted = [...teams]
  if (mode === 'name') return sorted.sort(byName)
  if (mode === 'frequency') {
    return sorted.sort((a, b) => (counts[b.id] ?? 0) - (counts[a.id] ?? 0) || byRecency(a, b))
  }
  return sorted.sort(byRecency)
}
